import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Image as ImageIcon,
  Star,
  Trash2,
  Replace,
  Upload,
  GripVertical,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { PERSONALIZED_PRODUCTS } from "@/lib/products";

// Simple, visual, Shopify-style image manager for the fixed 10-item catalog.
// Product name/price/description/category are NOT editable here by design —
// this panel only manages the photos attached to each product.
export default function CatalogImagesTab() {
  const [imagesByProduct, setImagesByProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [managing, setManaging] = useState(null); // product object currently open in the dialog

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/catalog-images");
      setImagesByProduct(res.data || {});
    } catch {
      toast.error("Failed to load catalog images");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div>
        <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Catalog</div>
        <h2 className="font-display text-3xl text-white mt-1">Product images</h2>
        <p className="text-sm text-white/50 mt-2 max-w-2xl">
          Upload, replace, delete or reorder photos for each of your 10 catalog products — changes
          go live on the site immediately. Names, prices and descriptions are managed in code and
          stay untouched here.
        </p>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-white/60 flex items-center gap-2">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PERSONALIZED_PRODUCTS.map((p) => {
              const imgs = imagesByProduct[p.id] || [];
              const cover = imgs[0]?.url || p.image;
              return (
                <div
                  key={p.id}
                  className="glass rounded-2xl overflow-hidden"
                  data-testid={`catalog-image-card-${p.id}`}
                >
                  <div className="aspect-[4/5] bg-black relative">
                    {cover ? (
                      <img src={cover} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20">
                        <ImageIcon size={40} />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest-2 text-white/80 glass px-2 py-1 rounded-full">
                      {imgs.length || 0} photo{imgs.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-display text-lg text-white line-clamp-1">{p.name}</div>
                    <div className="text-xs text-white/50 mt-1">{p.tag}</div>
                    <Button
                      onClick={() => setManaging(p)}
                      className="mt-4 w-full rounded-full bg-[#d4af37] text-black hover:brightness-110"
                      data-testid={`manage-images-${p.id}`}
                    >
                      Manage images
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ManageImagesDialog
        product={managing}
        images={managing ? imagesByProduct[managing.id] || [] : []}
        onOpenChange={(open) => !open && setManaging(null)}
        onChanged={(images) =>
          setImagesByProduct((s) => ({ ...s, [managing.id]: images }))
        }
      />
    </div>
  );
}

function ManageImagesDialog({ product, images, onOpenChange, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null); // image id currently being deleted/replaced/primary'd
  const [dragIdx, setDragIdx] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // image id pending delete confirmation

  if (!product) return null;

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await api.post(`/admin/catalog-images/${product.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChanged(res.data.images);
      toast.success(`${files.length} image${files.length === 1 ? "" : "s"} uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const replace = async (imageId, file) => {
    if (!file) return;
    setBusyId(imageId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/catalog-images/${product.id}/${imageId}/replace`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChanged(res.data.images);
      toast.success("Image replaced");
    } catch {
      toast.error("Replace failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (imageId) => {
    setBusyId(imageId);
    try {
      const res = await api.delete(`/admin/catalog-images/${product.id}/${imageId}`);
      onChanged(res.data.images);
      toast.success("Image deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  const setPrimary = async (imageId) => {
    setBusyId(imageId);
    try {
      const res = await api.put(`/admin/catalog-images/${product.id}/primary/${imageId}`);
      onChanged(res.data.images);
      toast.success("Primary image updated");
    } catch {
      toast.error("Failed to set primary image");
    } finally {
      setBusyId(null);
    }
  };

  const reorder = async (nextImages) => {
    onChanged(nextImages); // optimistic
    try {
      await api.put(`/admin/catalog-images/${product.id}/reorder`, {
        image_ids: nextImages.map((i) => i.id),
      });
    } catch {
      toast.error("Reorder failed to save");
    }
  };

  const onDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) return;
    const next = [...images];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    setDragIdx(null);
    reorder(next);
  };

  return (
    <Dialog open={!!product} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-transparent border-white/10 p-0 overflow-hidden">
        <div className="glass-strong rounded-2xl p-6 md:p-8 max-h-[85vh] overflow-y-auto">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-3xl text-white">{product.name}</DialogTitle>
            <DialogDescription className="text-xs uppercase tracking-widest-2 text-white/40 mt-1">
              {images.length} image{images.length === 1 ? "" : "s"} · drag to reorder · first image is the cover
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6">
            <label
              className={`inline-flex items-center gap-2 rounded-full glass border border-white/10 hover:border-[#d4af37] px-5 py-2.5 text-sm text-white/80 ${uploading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <Upload size={14} />
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                disabled={uploading}
                onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
                className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed w-0 h-0"
                data-testid="catalog-images-upload-input"
              />
              {uploading ? "Uploading…" : "Upload images"}
            </label>
            <span className="ml-3 text-[11px] text-white/40">JPG, PNG, WEBP · optimized automatically</span>
          </div>

          {images.length === 0 ? (
            <div className="mt-6 glass rounded-2xl p-10 text-center text-white/50">
              No images yet — upload your first photo above.
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
              {images.map((img, idx) => (
                <div
                  key={img.id}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(idx)}
                  className={`relative aspect-square rounded-xl overflow-hidden bg-white/5 border group cursor-grab active:cursor-grabbing ${idx === 0 ? "border-[#d4af37]" : "border-white/10"}`}
                  data-testid={`catalog-image-tile-${idx}`}
                >
                  <img src={img.url} alt="" className="w-full h-full object-cover" draggable={false} />
                  {busyId === img.id && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="animate-spin text-white" size={20} />
                    </div>
                  )}
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
                    <GripVertical size={12} className="text-white/50" />
                    {idx === 0 && (
                      <span className="text-[9px] tracking-widest-2 uppercase px-1.5 py-0.5 rounded-full bg-[#d4af37] text-black font-medium">
                        Primary
                      </span>
                    )}
                  </div>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-black/55 flex items-center justify-center gap-1.5">
                    {idx !== 0 && (
                      <button
                        type="button"
                        onClick={() => setPrimary(img.id)}
                        title="Set as primary"
                        className="w-7 h-7 rounded-full glass flex items-center justify-center text-white hover:bg-[#d4af37] hover:text-black transition"
                        data-testid={`set-primary-${idx}`}
                      >
                        <Star size={12} />
                      </button>
                    )}
                    <label
                      title="Replace"
                      className="w-7 h-7 rounded-full glass flex items-center justify-center text-white hover:bg-[#d4af37] hover:text-black transition cursor-pointer"
                    >
                      <Replace size={12} />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => { replace(img.id, e.target.files?.[0]); e.target.value = ""; }}
                        className="hidden"
                        data-testid={`replace-image-${idx}`}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(img.id)}
                      title="Delete"
                      className="w-7 h-7 rounded-full glass flex items-center justify-center text-white hover:bg-red-500 transition"
                      data-testid={`delete-image-${idx}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 flex justify-end">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/60 hover:text-white"
              data-testid="catalog-images-done"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-sm bg-transparent border-white/10 p-0 overflow-hidden">
          <div className="glass-strong rounded-2xl p-6 text-center">
            <DialogTitle className="text-white text-lg font-normal">
              Are you sure you want to delete this image?
            </DialogTitle>
            <DialogDescription className="text-white/50 text-xs mt-1">
              This removes it permanently from the product's gallery.
            </DialogDescription>
            <div className="mt-6 flex justify-center gap-3">
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(null)}
                className="text-white/60 hover:text-white"
                data-testid="delete-image-cancel"
              >
                <X size={14} className="mr-1" /> Cancel
              </Button>
              <Button
                onClick={() => remove(confirmDelete)}
                className="rounded-full bg-red-500 hover:bg-red-600 text-white"
                data-testid="delete-image-confirm"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
